import React from 'react'
import { Card, CardContent, FormInput } from '../ui'
import { Button } from '../ui/Button'
import { useAuth } from '../AuthProvider'
import { useI18n } from '../../i18n'
import { getActiveOrgId } from '../../utils/orgUtils'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'
import {
  APPLICABILITY_OPTIONS,
  IMPLEMENTATION_OPTIONS,
} from './informationSystemGrcConstants'
import CanonicalControlPicker from './CanonicalControlPicker'
import {
  fetchStandardScopes,
  createStandardScope,
  deleteStandardScope,
  fetchControlLinks,
  createControlLink,
  deleteControlLink,
  fetchEvidenceLinks,
  createEvidenceLink,
  deleteEvidenceLink,
  fetchRemediationLinks,
  createRemediationLink,
  deleteRemediationLink,
  fetchEnabledStandards,
  fetchOrgEvidenceOptions,
  fetchOrgRemediationOptions,
  fetchGrcSummaries,
  type StandardScope,
  type ControlLink,
  type EvidenceLink,
  type RemediationLink,
  type GrcSummaryProjection,
} from '../../services/informationSystemComplianceApi'
import { featureFlags } from '../../config/featureFlags'
import { fetchEffectiveStandardAccess } from '../../services/standardAccessApi'
import { toUserFacingStandardCode } from '../../utils/standardCanonicalUx'
import { isStandardAccessPolicyV1Enabled, pickGrantedStandardCodes } from '../../utils/standardAccessPolicy'
import OperationalWorkflowButton from '../compliance-workflow/OperationalWorkflowButton'
import {
  findComplianceScopeEntry,
  formatComplianceScopePrimary,
  mergeComplianceScopeEntries,
} from '../../utils/complianceScopeDisplay'
import { fetchOrgComplianceScopeByStandards } from '../../services/complianceAuthorityApi'
import type { EffectiveStandardAccess } from '../../services/standardAccessApi'
import InformationSystemApplicabilityPanel from './InformationSystemApplicabilityPanel'
import InformationSystemMaterializedReadinessPanel from './InformationSystemMaterializedReadinessPanel'
import InformationSystemCrossStandardReusePanel from '../compliance-reuse/InformationSystemCrossStandardReusePanel'
import InformationSystemOperationalAssurancePanel from '../control-assurance/InformationSystemOperationalAssurancePanel'
import InformationSystemExecutiveGovernancePanel from '../executive-governance/InformationSystemExecutiveGovernancePanel'
import InformationSystemOperationalEventsPanel from '../compliance-runtime/InformationSystemOperationalEventsPanel'

export type GrcVisibleSection = 'compliance' | 'controls' | 'evidence' | 'remediation' | 'readiness-summary'

interface Props {
  systemId: string
  onSummaryLoaded?: (summary: GrcSummaryProjection) => void
  visibleSection?: GrcVisibleSection | null
}

export default function InformationSystemGrcSections({ systemId, onSummaryLoaded, visibleSection }: Props) {
  const { t } = useI18n()
  const { token } = useAuth()
  const orgId = getActiveOrgId()

  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [standards, setStandards] = React.useState<StandardScope[]>([])
  const [controls, setControls] = React.useState<ControlLink[]>([])
  const [evidence, setEvidence] = React.useState<EvidenceLink[]>([])
  const [remediation, setRemediation] = React.useState<RemediationLink[]>([])
  const [summary, setSummary] = React.useState<GrcSummaryProjection | null>(null)
  const [enabledStandards, setEnabledStandards] = React.useState<string[]>([])
  const [grantedStandards, setGrantedStandards] = React.useState<string[] | null>(null)
  const [standardsApiFailed, setStandardsApiFailed] = React.useState(false)
  const [evidenceOptions, setEvidenceOptions] = React.useState<Array<{ id: string; title: string }>>([])
  const [remediationOptions, setRemediationOptions] = React.useState<
    Array<{ id: string; title: string; kind: 'task' | 'recommendation' }>
  >([])

  const [newStandard, setNewStandard] = React.useState('')
  const [newApplicability, setNewApplicability] = React.useState('IN_SCOPE')
  const [ctrlStandard, setCtrlStandard] = React.useState('')
  const [ctrlId, setCtrlId] = React.useState('')
  const [ctrlTitle, setCtrlTitle] = React.useState('')
  const [ctrlStatus, setCtrlStatus] = React.useState('NOT_STARTED')
  const [evidencePick, setEvidencePick] = React.useState('')
  const [remediationPick, setRemediationPick] = React.useState('')
  const [controlFilter, setControlFilter] = React.useState('')

  const load = React.useCallback(async () => {
    if (!token) return
    setLoading(true)
    setError(null)
    try {
      const [scopes, ctrls, evs, rems, summaries, enabled, evOpts, remOpts] = await Promise.all([
        fetchStandardScopes(token, systemId, orgId),
        fetchControlLinks(token, systemId, orgId),
        fetchEvidenceLinks(token, systemId, orgId),
        fetchRemediationLinks(token, systemId, orgId),
        fetchGrcSummaries(token, orgId).then((rows) => rows.find((r) => r.informationSystemId === systemId) || null),
        fetchEnabledStandards(token),
        fetchOrgEvidenceOptions(token),
        fetchOrgRemediationOptions(token),
      ])
      setStandards(scopes)
      setControls(ctrls)
      setEvidence(evs)
      setRemediation(rems)
      if (summaries) {
        const inScopeCodes = scopes
          .filter((s) => String(s.applicabilityStatus || '').toUpperCase() === 'IN_SCOPE')
          .map((s) => s.standardCode)
        const orgScope = inScopeCodes.length
          ? await fetchOrgComplianceScopeByStandards(inScopeCodes).catch(() => [])
          : []
        const enriched = {
          ...summaries,
          complianceScope: mergeComplianceScopeEntries(
            summaries.complianceScope,
            orgScope,
          ),
        }
        setSummary(enriched)
        onSummaryLoaded?.(enriched)
      }
      setEnabledStandards(enabled)
      setStandardsApiFailed(enabled.length === 0)
      setEvidenceOptions(evOpts)
      setRemediationOptions(remOpts)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('assets.grc.error.load', 'Failed to load compliance data'))
    } finally {
      setLoading(false)
    }
  }, [token, systemId, orgId, t, onSummaryLoaded])

  React.useEffect(() => {
    load()
  }, [load])

  React.useEffect(() => {
    if (!isStandardAccessPolicyV1Enabled() || !token || !orgId) return
    void fetchEffectiveStandardAccess(orgId)
      .then((effective) => setGrantedStandards(effective.standardCodes || []))
      .catch(() => setGrantedStandards(null))
  }, [token, orgId])

  const pickerStandards = React.useMemo(() => {
    if (!isStandardAccessPolicyV1Enabled() || grantedStandards === null) return enabledStandards
    return pickGrantedStandardCodes(
      {
        organizationId: orgId || '',
        userId: '',
        standardCodes: grantedStandards,
        grantedStandards: [],
        organizationGrants: [],
        userGrants: [],
      },
      enabledStandards,
    )
  }, [enabledStandards, grantedStandards, orgId])

  const grantedSet = React.useMemo(
    () => new Set((grantedStandards || []).map((c) => c.toUpperCase())),
    [grantedStandards],
  )

  const effectiveAccessForWorkflow = React.useMemo<EffectiveStandardAccess | null>(() => {
    if (!orgId || !grantedStandards) return null
    return {
      organizationId: orgId,
      userId: '',
      standardCodes: grantedStandards,
    }
  }, [grantedStandards, orgId])

  const filteredControls = controls.filter(
    (c) => !controlFilter || c.standardCode === controlFilter
  )

  const topologyStandardCode = React.useMemo(() => {
    if (controlFilter) return controlFilter
    const inScope = standards.find(
      (s) => s.applicabilityStatus === 'IN_SCOPE' || s.applicabilityStatus === 'APPLICABLE'
    )
    if (inScope?.standardCode) return inScope.standardCode
    if (standards[0]?.standardCode) return standards[0].standardCode
    const fromControls = controls[0]?.standardCode
    return fromControls || null
  }, [controlFilter, standards, controls])

  if (loading) {
    return <p className="text-sm text-gray-500">{t('common.loading')}</p>
  }

  const show = (section: GrcVisibleSection) => !visibleSection || visibleSection === section

  return (
    <div className="space-y-6">
      {error ? (
        <p className="text-sm text-red-600">
          {error}{' '}
          <Button type="button" size="small" color="outline" onClick={() => load()}>
            {t('common.retry', 'Retry')}
          </Button>
        </p>
      ) : null}

      {show('readiness-summary') ? <section id="passport-section-readiness-summary" className="scroll-mt-4">
        <h4 className="font-medium mb-2">{t('assets.grc.readinessSummary', 'Readiness summary')}</h4>
        {summary ? (
          <div className="text-sm space-y-2 border rounded p-3 bg-slate-50">
            <p>
              <span className="text-gray-500">{t('assets.grc.readinessStatus', 'Readiness')}:</span>{' '}
              <span className="font-medium">
                {t(`assets.grc.readiness.${summary.readinessStatus}`, summary.readinessStatus)}
              </span>
            </p>
            {summary.controlCoveragePercent != null ? (
              <p>
                {t('assets.grc.controlCoverage', 'Control coverage')}: {summary.controlCoveragePercent}%
              </p>
            ) : null}
            <p>
              {t('assets.grc.evidenceCount', 'Evidence linked')}: {summary.evidenceLinkedCount}
              {summary.evidenceVerifiedCount != null
                ? ` (${summary.evidenceVerifiedCount} ${t('assets.grc.verified', 'verified')})`
                : ''}
            </p>
            <p>
              {t('assets.grc.remediationOpen', 'Open remediation')}: {summary.remediationOpenCount}
            </p>
            <ul className="text-xs text-gray-600 list-disc pl-4">
              {(summary.explanation || []).map((ex) => (
                <li key={ex.code}>{t(`assets.grc.reason.${ex.code}`, ex.message)}</li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-sm text-gray-500 italic">{t('common.unknown', 'Unknown')}</p>
        )}
      </section> : null}

      {show('readiness-summary') && featureFlags.COMPLIANCE_REUSE_V1 ? (
        <section id="passport-section-cross-standard-reuse" className="scroll-mt-4 mt-4">
          <InformationSystemCrossStandardReusePanel
            informationSystemId={systemId}
            standardCodes={standards.map((s) => s.standardCode)}
          />
        </section>
      ) : null}

      {show('readiness-summary') && featureFlags.CONTROL_ASSURANCE_V1 ? (
        <section id="passport-section-operational-assurance" className="scroll-mt-4 mt-4">
          <InformationSystemOperationalAssurancePanel standardCodes={standards.map((s) => s.standardCode)} />
        </section>
      ) : null}

      {show('readiness-summary') && featureFlags.EXECUTIVE_GOVERNANCE_V1 ? (
        <section id="passport-section-executive-governance" className="scroll-mt-4 mt-4">
          <InformationSystemExecutiveGovernancePanel informationSystemId={systemId} />
        </section>
      ) : null}

      {show('readiness-summary') && featureFlags.COMPLIANCE_RUNTIME_V1 && standards[0] ? (
        <section id="passport-section-operational-events" className="scroll-mt-4 mt-4">
          <InformationSystemOperationalEventsPanel standardCode={standards[0].standardCode} />
        </section>
      ) : null}

      {show('compliance') ? <section id="passport-section-compliance" className="scroll-mt-4">
        <h4 className={`${listStyles.listRowTitle} mb-3`}>
          {t('assets.grc.complianceScope', 'Compliance scope')}
        </h4>
        {standardsApiFailed ? (
          <p className="text-xs text-amber-700 mb-2">
            {t('assets.grc.standardsUnavailable', 'Standards catalog unavailable. Enter standard code manually.')}
          </p>
        ) : null}
        {standards.length === 0 ? (
          <p className="text-sm text-gray-500 mb-2">{t('assets.grc.emptyStandards', 'No standards assigned yet.')}</p>
        ) : (
          <ul className={`${listStyles.listStack} mb-3`}>
            {standards.map((s) => {
              const revoked =
                featureFlags.STANDARD_ACCESS_POLICY_V1 &&
                grantedStandards &&
                grantedStandards.length > 0 &&
                !grantedSet.has(s.standardCode.toUpperCase())
              const scope = findComplianceScopeEntry(summary?.complianceScope, s.standardCode)
              return (
              <li key={s.id} className={listStyles.listRow}>
                <div className={listStyles.listRowMain}>
                  <p className={listStyles.listRowTitle}>{toUserFacingStandardCode(s.standardCode)}</p>
                  <p className={listStyles.listRowMeta}>
                    {t(`assets.grc.applicability.${s.applicabilityStatus}`, s.applicabilityStatus)}
                  </p>
                  {scope ? (
                    <p className={listStyles.listRowMeta}>{formatComplianceScopePrimary(scope, t)}</p>
                  ) : null}
                  {revoked ? (
                    <p className={`${listStyles.listRowMeta} text-amber-700`}>
                      ({t('standardGovernance.notGranted')})
                    </p>
                  ) : null}
                </div>
                <div className={listStyles.listRowActions}>
                <OperationalWorkflowButton
                  standardCode={s.standardCode}
                  effectiveAccess={effectiveAccessForWorkflow}
                />
                <Button
                  type="button"
                  size="small"
                  color="outline"
                  onClick={async () => {
                    if (!token) return
                    await deleteStandardScope(token, systemId, s.id, orgId)
                    await load()
                  }}
                >
                  {t('common.remove', 'Remove')}
                </Button>
                </div>
              </li>
            )})}
          </ul>
        )}
        <div className="flex flex-wrap gap-2 items-end">
          {pickerStandards.length > 0 ? (
            <select className="border rounded px-2 py-1 text-sm" value={newStandard} onChange={(e) => setNewStandard(e.target.value)}>
              <option value="">{t('assets.grc.selectStandard', 'Select standard…')}</option>
              {pickerStandards.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          ) : (
            <FormInput
              className="max-w-[140px]"
              placeholder={t('assets.grc.standardCode', 'Standard code')}
              value={newStandard}
              onChange={(e) => setNewStandard(e.target.value)}
            />
          )}
          <select className="border rounded px-2 py-1 text-sm" value={newApplicability} onChange={(e) => setNewApplicability(e.target.value)}>
            {APPLICABILITY_OPTIONS.map((a) => (
              <option key={a} value={a}>
                {t(`assets.grc.applicability.${a}`, a)}
              </option>
            ))}
          </select>
          <Button
            type="button"
            size="small"
            disabled={!newStandard.trim()}
            onClick={async () => {
              if (!token || !newStandard.trim()) return
              await createStandardScope(token, systemId, { standardCode: newStandard, applicabilityStatus: newApplicability }, orgId)
              setNewStandard('')
              await load()
            }}
          >
            {t('common.add', 'Add')}
          </Button>
        </div>
      </section> : null}

      {show('controls') ? <section id="passport-section-controls" className="scroll-mt-4">
        <h4 className="font-medium mb-2">{t('assets.grc.controls', 'Controls')}</h4>
        {controls.length === 0 ? (
          <p className="text-sm text-gray-500 mb-2">{t('assets.grc.emptyControls', 'No controls mapped to this system yet.')}</p>
        ) : (
          <>
            <select className="border rounded px-2 py-1 text-sm mb-2" value={controlFilter} onChange={(e) => setControlFilter(e.target.value)}>
              <option value="">{t('assets.grc.allStandards', 'All standards')}</option>
              {[...new Set(controls.map((c) => c.standardCode))].map((sc) => (
                <option key={sc} value={sc}>
                  {sc}
                </option>
              ))}
            </select>
            <ul className="text-sm divide-y mb-2 max-h-48 overflow-y-auto">
              {filteredControls.map((c) => (
                <li key={c.id} className="flex justify-between py-1 gap-2">
                  <span>
                    {c.standardCode} / {c.controlId}
                    {c.controlTitle ? ` — ${c.controlTitle}` : ''}
                    <span className="text-gray-500 ml-1">
                      ({t(`assets.grc.implementation.${c.implementationStatus}`, c.implementationStatus)})
                    </span>
                  </span>
                  <Button
                    type="button"
                    size="small"
                    color="outline"
                    onClick={async () => {
                      if (!token) return
                      await deleteControlLink(token, systemId, c.id, orgId)
                      await load()
                    }}
                  >
                    {t('common.remove', 'Remove')}
                  </Button>
                </li>
              ))}
            </ul>
          </>
        )}
        <CanonicalControlPicker
          standardCode={ctrlStandard}
          controlId={ctrlId}
          controlTitle={ctrlTitle}
          onSelect={(e) => {
            setCtrlId(e.controlId)
            setCtrlTitle(e.controlTitle)
          }}
          onControlIdChange={setCtrlId}
          onControlTitleChange={setCtrlTitle}
        />
        <div className="grid gap-2 sm:grid-cols-2">
          <FormInput placeholder={t('assets.grc.standardCode', 'Standard code')} value={ctrlStandard} onChange={(e) => setCtrlStandard(e.target.value)} />
          <FormInput placeholder={t('assets.grc.controlId', 'Control ID')} value={ctrlId} onChange={(e) => setCtrlId(e.target.value)} />
          <FormInput placeholder={t('assets.grc.controlTitle', 'Control title')} value={ctrlTitle} onChange={(e) => setCtrlTitle(e.target.value)} />
          <select className="border rounded px-2 py-1 text-sm" value={ctrlStatus} onChange={(e) => setCtrlStatus(e.target.value)}>
            {IMPLEMENTATION_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {t(`assets.grc.implementation.${s}`, s)}
              </option>
            ))}
          </select>
        </div>
        <Button
          type="button"
          size="small"
          className="mt-2"
          disabled={!ctrlStandard.trim() || !ctrlId.trim()}
          onClick={async () => {
            if (!token) return
            await createControlLink(
              token,
              systemId,
              { standardCode: ctrlStandard, controlId: ctrlId, controlTitle: ctrlTitle || undefined, implementationStatus: ctrlStatus },
              orgId
            )
            setCtrlId('')
            setCtrlTitle('')
            await load()
          }}
        >
          {t('assets.grc.mapControl', 'Map control')}
        </Button>
        {featureFlags.COMPLIANCE_TOPOLOGY_V1 ? (
          <InformationSystemApplicabilityPanel systemId={systemId} standardCode={topologyStandardCode} />
        ) : null}
        {featureFlags.COMPLIANCE_TOPOLOGY_READINESS_V1 ? (
          <InformationSystemMaterializedReadinessPanel systemId={systemId} standardCode={topologyStandardCode} />
        ) : null}
      </section> : null}

      {show('evidence') ? <section id="passport-section-evidence" className="scroll-mt-4">
        <h4 className="font-medium mb-2">{t('assets.grc.evidence', 'Evidence')}</h4>
        {evidenceOptions.length === 0 ? (
          <p className="text-xs text-gray-500 mb-2">{t('assets.grc.evidenceUnavailable', 'Governance evidence API unavailable or empty.')}</p>
        ) : null}
        {evidence.length === 0 ? (
          <p className="text-sm text-gray-500 mb-2">{t('assets.grc.emptyEvidence', 'No evidence linked yet.')}</p>
        ) : (
          <ul className="text-sm divide-y mb-2">
            {evidence.map((e) => (
              <li key={e.id} className="flex justify-between py-1 gap-2">
                <span>
                  {e.evidence?.title || e.evidenceId}
                  {e.evidence?.verificationStatus ? (
                    <span className="text-gray-500 ml-1">({e.evidence.verificationStatus})</span>
                  ) : null}
                </span>
                <Button
                  type="button"
                  size="small"
                  color="outline"
                  onClick={async () => {
                    if (!token) return
                    await deleteEvidenceLink(token, systemId, e.id, orgId)
                    await load()
                  }}
                >
                  {t('common.remove', 'Remove')}
                </Button>
              </li>
            ))}
          </ul>
        )}
        {evidenceOptions.length > 0 ? (
          <div className="flex gap-2">
            <select className="border rounded px-2 py-1 text-sm flex-1" value={evidencePick} onChange={(e) => setEvidencePick(e.target.value)}>
              <option value="">{t('assets.grc.selectEvidence', 'Select evidence…')}</option>
              {evidenceOptions.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.title}
                </option>
              ))}
            </select>
            <Button
              type="button"
              size="small"
              disabled={!evidencePick}
              onClick={async () => {
                if (!token || !evidencePick) return
                await createEvidenceLink(token, systemId, { evidenceId: evidencePick }, orgId)
                setEvidencePick('')
                await load()
              }}
            >
              {t('assets.grc.linkEvidence', 'Link')}
            </Button>
          </div>
        ) : null}
      </section> : null}

      {show('remediation') ? <section id="passport-section-remediation" className="scroll-mt-4">
        <h4 className="font-medium mb-2">{t('assets.grc.remediation', 'Remediation')}</h4>
        {remediationOptions.length === 0 ? (
          <p className="text-xs text-gray-500 mb-2">{t('assets.grc.remediationUnavailable', 'Remediation/recommendation API unavailable or empty.')}</p>
        ) : null}
        {remediation.length === 0 ? (
          <p className="text-sm text-gray-500 mb-2">{t('assets.grc.emptyRemediation', 'No remediation links yet.')}</p>
        ) : (
          <ul className="text-sm divide-y mb-2">
            {remediation.map((r) => (
              <li key={r.id} className="flex justify-between py-1 gap-2">
                <span>
                  {r.remediationTask?.title || r.recommendation?.title || r.id}
                  {(r.remediationTask?.status || r.recommendation?.status) && (
                    <span className="text-gray-500 ml-1">({r.remediationTask?.status || r.recommendation?.status})</span>
                  )}
                </span>
                <Button
                  type="button"
                  size="small"
                  color="outline"
                  onClick={async () => {
                    if (!token) return
                    await deleteRemediationLink(token, systemId, r.id, orgId)
                    await load()
                  }}
                >
                  {t('common.remove', 'Remove')}
                </Button>
              </li>
            ))}
          </ul>
        )}
        {remediationOptions.length > 0 ? (
          <div className="flex gap-2">
            <select className="border rounded px-2 py-1 text-sm flex-1" value={remediationPick} onChange={(e) => setRemediationPick(e.target.value)}>
              <option value="">{t('assets.grc.selectRemediation', 'Select task or recommendation…')}</option>
              {remediationOptions.map((o) => (
                <option key={`${o.kind}-${o.id}`} value={`${o.kind}:${o.id}`}>
                  [{o.kind}] {o.title}
                </option>
              ))}
            </select>
            <Button
              type="button"
              size="small"
              disabled={!remediationPick}
              onClick={async () => {
                if (!token || !remediationPick) return
                const [kind, id] = remediationPick.split(':')
                const payload =
                  kind === 'task' ? { remediationTaskId: id } : { recommendationId: id }
                await createRemediationLink(token, systemId, payload, orgId)
                setRemediationPick('')
                await load()
              }}
            >
              {t('assets.grc.linkRemediation', 'Link')}
            </Button>
          </div>
        ) : null}
      </section> : null}
    </div>
  )
}
